import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Clock,
  Plus,
  Download,
  RefreshCw,
  Pencil,
  Trash2,
  Copy,
  TrendingUp,
  Calendar,
  Target,
  ChevronLeft,
  ChevronRight,
  List,
  LayoutGrid,
  Zap,
  Info,
} from "lucide-react";
import { format, parseISO, addDays, startOfWeek } from "date-fns";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Constants ──────────────────────────────────────────────────────────────

const PROJECTS = ["The Better Fare", "Restricted Deals", "Other"];

const ACTIVITY_TYPES = [
  "customer support",
  "flight search",
  "visa processing",
  "bookkeeping",
  "website development",
  "marketing / SEO",
  "affiliate / supplier work",
  "content creation",
  "admin / tax",
  "other",
];

const DESCRIPTION_PLACEHOLDERS = {
  "customer support":       "Customer WhatsApp support and flight enquiry handling",
  "flight search":          "Searched and priced 3 flights for Amsterdam–Dubai–Manila",
  "visa processing":        "Processed UK tourist visa applications for 2 clients",
  "bookkeeping":            "VAT reconciliation and quarterly bookkeeping review",
  "website development":    "Implemented invoice workflow and payment integration",
  "marketing / SEO":        "Created and published 4 flight deal posts on social media",
  "affiliate / supplier work": "Updated affiliate partner rates and supplier agreements",
  "content creation":       "Wrote 3 destination guides for The Better Fare blog",
  "admin / tax":            "Annual tax return preparation and urencriterium documentation",
  "other":                  "Add a clear description for your tax records",
};

const PROJECT_COLORS = {
  "The Better Fare": "bg-violet-100 text-violet-700",
  "Restricted Deals": "bg-blue-100 text-blue-700",
  "Other": "bg-slate-100 text-slate-600",
};

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ── Utilities ──────────────────────────────────────────────────────────────

const fmtH = (h) => {
  if (!h && h !== 0) return "0h";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

function calcHours(start, end, brk) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm) - Math.max(0, Number(brk) || 0);
  return mins > 0 ? Math.round((mins / 60) * 100) / 100 : null;
}

function getWeekMonday(dateOrStr) {
  const d = typeof dateOrStr === "string" ? parseISO(dateOrStr) : new Date(dateOrStr);
  return startOfWeek(d, { weekStartsOn: 1 });
}

function getWeekDays(monday) {
  return Array.from({ length: 7 }, (_, i) =>
    format(addDays(monday, i), "yyyy-MM-dd")
  );
}

function formatWeekRange(days) {
  const s = parseISO(days[0]);
  const e = parseISO(days[6]);
  if (format(s, "MMM yyyy") === format(e, "MMM yyyy")) {
    return `${format(s, "d")} – ${format(e, "d MMM yyyy")}`;
  }
  return `${format(s, "d MMM")} – ${format(e, "d MMM yyyy")}`;
}

function fmtAudit(isoStr) {
  if (!isoStr) return null;
  try { return format(parseISO(isoStr), "d MMM yyyy"); }
  catch { return null; }
}

const EMPTY_FORM = {
  date: todayStr(),
  project: "The Better Fare",
  activity_type: "customer support",
  description: "",
  start_time: "",
  end_time: "",
  break_minutes: 0,
  total_hours: "",
  billable: true,
  related_reference: "",
};

// ── Shared description warning ─────────────────────────────────────────────

function DescWarning({ description }) {
  if (!description || description.trim().length === 0 || description.trim().length >= 10) return null;
  return (
    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
      <Info className="w-3 h-3 shrink-0" />
      Add a more specific description for tax records.
    </p>
  );
}

// ── Quick Log Modal ────────────────────────────────────────────────────────

function QuickLogModal({ open, onClose, onSaved, defaultDate }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY_FORM, date: defaultDate || todayStr() });
  }, [open, defaultDate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.date)          { toast.error("Date is required"); return; }
    if (!form.project)       { toast.error("Project is required"); return; }
    if (!form.activity_type) { toast.error("Activity type is required"); return; }
    const h = Number(form.total_hours);
    if (!form.total_hours || isNaN(h) || h <= 0) { toast.error("Total hours is required"); return; }

    setSaving(true);
    try {
      await axios.post(`${API}/working-hours`, {
        ...form,
        total_hours:   h,
        start_time:    null,
        end_time:      null,
        break_minutes: 0,
      }, { headers: getAuthHeaders() });
      toast.success("Logged!");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            Quick Log
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={e => set("date", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Hours *</Label>
              <Input
                type="number" min="0.25" step="0.25"
                value={form.total_hours}
                onChange={e => set("total_hours", e.target.value)}
                placeholder="e.g. 2.5"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>Project *</Label>
            <Select value={form.project} onValueChange={v => set("project", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROJECTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Activity Type *</Label>
            <Select value={form.activity_type} onValueChange={v => set("activity_type", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map(a => (
                  <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              value={form.description}
              onChange={e => set("description", e.target.value)}
              placeholder={DESCRIPTION_PLACEHOLDERS[form.activity_type] || "What did you work on?"}
              className="mt-1"
            />
            <DescWarning description={form.description} />
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label>Billable</Label>
              <div className="flex gap-3 mt-2">
                {[true, false].map(v => (
                  <label key={String(v)} className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <input type="radio" checked={form.billable === v} onChange={() => set("billable", v)} className="accent-purple-600" />
                    {v ? "Yes" : "No"}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Reference <span className="text-slate-400 font-normal text-xs">(opt.)</span></Label>
              <Input value={form.related_reference} onChange={e => set("related_reference", e.target.value)} placeholder="Ref #" className="mt-1" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white">
            {saving ? "Saving…" : "Log Hours"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Full Entry Form Modal ──────────────────────────────────────────────────

function EntryModal({ open, onClose, onSaved, initial }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY_FORM, ...initial } : EMPTY_FORM);
    }
  }, [open, initial]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const derivedHours = calcHours(form.start_time, form.end_time, form.break_minutes);
  const displayHours = derivedHours !== null ? derivedHours : (form.total_hours === "" ? "" : form.total_hours);

  const handleSave = async () => {
    const hours = derivedHours !== null ? derivedHours : Number(form.total_hours);
    if (!form.date)          { toast.error("Date is required"); return; }
    if (!form.project)       { toast.error("Project is required"); return; }
    if (!form.activity_type) { toast.error("Activity type is required"); return; }
    if (form.start_time && form.end_time && derivedHours === null) {
      toast.error("End time must be after start time (after break)"); return;
    }
    if (!form.start_time && (form.total_hours === "" || isNaN(Number(form.total_hours)))) {
      toast.error("Enter start/end times or a manual total hours value"); return;
    }
    if (hours < 0) { toast.error("Total hours cannot be negative"); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        break_minutes: Number(form.break_minutes) || 0,
        total_hours: derivedHours !== null ? derivedHours : Number(form.total_hours),
        start_time:  form.start_time || null,
        end_time:    form.end_time   || null,
      };
      if (initial?.id) {
        await axios.patch(`${API}/working-hours/${initial.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/working-hours`, payload, { headers: getAuthHeaders() });
        toast.success("Entry added");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isEditing = !!initial?.id;
  const workDate  = isEditing && initial.date   ? fmtAudit(initial.date)       : null;
  const createdAt = isEditing && initial.created_at ? fmtAudit(initial.created_at) : null;
  const updatedAt = isEditing && initial.updated_at ? fmtAudit(initial.updated_at) : null;

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Entry" : "Add Working Hours"}</DialogTitle>
        </DialogHeader>

        {/* Audit info when editing */}
        {isEditing && (createdAt || updatedAt) && (
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-400 bg-slate-50 rounded-md px-3 py-2 -mt-1">
            {createdAt && <span>Recorded: <span className="font-medium text-slate-600">{createdAt}</span></span>}
            {updatedAt && updatedAt !== createdAt && <span>Last edited: <span className="font-medium text-slate-600">{updatedAt}</span></span>}
          </div>
        )}

        <div className="space-y-4 py-2">
          {/* Date + Project */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Work Date *</Label>
              <Input type="date" value={form.date} onChange={e => set("date", e.target.value)} className="mt-1" />
              {isEditing && initial.date !== todayStr() && (
                <p className="text-xs text-slate-400 mt-0.5">Backdated entry</p>
              )}
            </div>
            <div>
              <Label>Project *</Label>
              <Select value={form.project} onValueChange={v => set("project", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROJECTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Activity */}
          <div>
            <Label>Activity Type *</Label>
            <Select value={form.activity_type} onValueChange={v => set("activity_type", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map(a => (
                  <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label>Description / Notes</Label>
            <Input
              value={form.description}
              onChange={e => set("description", e.target.value)}
              placeholder={DESCRIPTION_PLACEHOLDERS[form.activity_type] || "What did you work on?"}
              className="mt-1"
            />
            <DescWarning description={form.description} />
          </div>

          {/* Times */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Start Time</Label>
              <Input type="time" value={form.start_time || ""} onChange={e => set("start_time", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>End Time</Label>
              <Input type="time" value={form.end_time || ""} onChange={e => set("end_time", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Break (min)</Label>
              <Input type="number" min="0" value={form.break_minutes} onChange={e => set("break_minutes", e.target.value)} className="mt-1" />
            </div>
          </div>

          {/* Total hours + billable */}
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <Label>
                Total Hours
                {derivedHours !== null && (
                  <span className="ml-2 text-xs text-emerald-600 font-normal">(auto)</span>
                )}
              </Label>
              <Input
                type="number" min="0" step="0.25"
                value={displayHours}
                disabled={derivedHours !== null}
                onChange={e => set("total_hours", e.target.value)}
                placeholder={derivedHours !== null ? String(derivedHours) : "e.g. 2.5"}
                className={`mt-1 ${derivedHours !== null ? "bg-slate-50 text-slate-500" : ""}`}
              />
            </div>
            <div className="pb-0.5">
              <Label className="block mb-1.5">Billable</Label>
              <div className="flex gap-3">
                {[true, false].map(v => (
                  <label key={String(v)} className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <input type="radio" name="billable" checked={form.billable === v} onChange={() => set("billable", v)} className="accent-purple-600" />
                    {v ? "Billable" : "Non-billable"}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Related reference */}
          <div>
            <Label>Related Reference <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              value={form.related_reference || ""}
              onChange={e => set("related_reference", e.target.value)}
              placeholder="Booking #, Invoice #, Visa Quote #, deal ref…"
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
            {saving ? "Saving…" : isEditing ? "Save Changes" : "Add Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Day Section (weekly view) ──────────────────────────────────────────────

function DaySection({ dateStr, dayName, entries, onAddForDay, onEdit, onDeleteRequest }) {
  const dayTotal = entries.reduce((s, e) => s + (e.total_hours || 0), 0);
  const isToday  = dateStr === todayStr();
  const isPast   = dateStr < todayStr();

  return (
    <div className={`border rounded-xl overflow-hidden ${isToday ? "border-purple-300 ring-1 ring-purple-200" : "border-slate-200"}`}>
      {/* Day header */}
      <div className={`flex items-center justify-between px-4 py-2.5 ${isToday ? "bg-purple-50" : "bg-slate-50"}`}>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${isToday ? "text-purple-700" : "text-slate-700"}`}>
            {dayName}
          </span>
          <span className="text-xs text-slate-400">
            {format(parseISO(dateStr), "d MMM")}
          </span>
          {isToday && <Badge className="text-xs bg-purple-100 text-purple-700 h-4 px-1.5">Today</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {dayTotal > 0 && (
            <span className="text-xs font-semibold text-slate-600">{fmtH(dayTotal)}</span>
          )}
          <Button
            size="sm" variant="ghost"
            className={`h-7 text-xs px-2 ${isToday ? "text-purple-600 hover:bg-purple-100" : "text-slate-500 hover:bg-slate-200"}`}
            onClick={() => onAddForDay(dateStr)}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Entries */}
      {entries.length === 0 ? (
        <div className="px-4 py-3 text-xs text-slate-400 italic">No entries</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {entries.map(entry => (
            <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 group">
              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded shrink-0 ${PROJECT_COLORS[entry.project] || "bg-slate-100 text-slate-600"}`}>
                {entry.project === "The Better Fare" ? "TBF" : entry.project === "Restricted Deals" ? "RD" : "Other"}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 capitalize truncate">{entry.activity_type}</p>
                {entry.description && (
                  <p className="text-xs text-slate-400 truncate">{entry.description}</p>
                )}
              </div>
              {entry.start_time && entry.end_time && (
                <span className="text-xs font-mono text-slate-400 shrink-0">{entry.start_time}–{entry.end_time}</span>
              )}
              <span className="text-xs font-bold text-slate-700 shrink-0 w-10 text-right">{fmtH(entry.total_hours || 0)}</span>
              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onEdit(entry)} title="Edit">
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => onDeleteRequest(entry.id)} title="Delete">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Weekly View ────────────────────────────────────────────────────────────

function WeekView({ weekStart, setWeekStart, onAddForDay, onEdit, onDeleteRequest, entries, loading }) {
  const weekDays   = getWeekDays(weekStart);
  const weekTotal  = entries.reduce((s, e) => s + (e.total_hours || 0), 0);
  const isThisWeek = weekDays[0] === format(getWeekMonday(new Date()), "yyyy-MM-dd");

  const byDay = {};
  weekDays.forEach(d => { byDay[d] = []; });
  entries.forEach(e => { if (byDay[e.date]) byDay[e.date].push(e); });

  return (
    <div>
      {/* Week navigation */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" size="sm" onClick={() => setWeekStart(d => addDays(d, -7))}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-semibold text-slate-700 min-w-[180px] text-center">
          {formatWeekRange(weekDays)}
        </span>
        <Button variant="outline" size="sm" onClick={() => setWeekStart(d => addDays(d, 7))}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        {!isThisWeek && (
          <Button variant="ghost" size="sm" className="text-purple-600 text-xs" onClick={() => setWeekStart(getWeekMonday(new Date()))}>
            This week
          </Button>
        )}
        <div className="ml-auto text-sm text-slate-500">
          Week total: <span className="font-bold text-slate-800">{fmtH(weekTotal)}</span>
        </div>
      </div>

      {/* Day sections */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-purple-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {weekDays.map((d, i) => (
            <DaySection
              key={d}
              dateStr={d}
              dayName={DAY_NAMES[i]}
              entries={byDay[d] || []}
              onAddForDay={onAddForDay}
              onEdit={onEdit}
              onDeleteRequest={onDeleteRequest}
            />
          ))}
        </div>
      )}

      {/* Weekly footer total */}
      {!loading && weekTotal > 0 && (
        <div className="mt-4 text-right text-sm text-slate-500">
          Weekly total: <span className="font-bold text-slate-800 text-base">{fmtH(weekTotal)}</span>
        </div>
      )}
    </div>
  );
}

// ── Summary Cards ──────────────────────────────────────────────────────────

function SummaryCards({ summary, loading }) {
  if (loading) return (
    <div className="flex items-center justify-center h-24">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
    </div>
  );
  if (!summary) return null;

  const cards = [
    { label: "This Week",    value: fmtH(summary.week),    icon: Calendar,   color: "text-blue-600",    bg: "bg-blue-50" },
    { label: "This Month",   value: fmtH(summary.month),   icon: TrendingUp, color: "text-violet-600",  bg: "bg-violet-50" },
    { label: "This Quarter", value: fmtH(summary.quarter), icon: TrendingUp, color: "text-amber-600",   bg: "bg-amber-50" },
    { label: "This Year",    value: fmtH(summary.year),    icon: Clock,      color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <Card key={c.label}>
          <CardContent className="p-4">
            <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center mb-3`}>
              <c.icon className={`w-4 h-4 ${c.color}`} />
            </div>
            <p className="text-2xl font-bold text-slate-900">{c.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Urencriterium Card ─────────────────────────────────────────────────────

function UrenCard({ data }) {
  if (!data) return null;
  const pct = data.pct;
  const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-violet-500" : pct >= 30 ? "bg-amber-500" : "bg-red-400";

  return (
    <Card className="mb-6 border-amber-200 bg-amber-50/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
          <Target className="w-4 h-4" />
          Dutch Urencriterium — {new Date().getFullYear()} Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap gap-6 mb-3">
          <div>
            <p className="text-2xl font-bold text-slate-900">{fmtH(data.logged)}</p>
            <p className="text-xs text-slate-500">logged of {data.target} hrs target</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{fmtH(data.remaining)}</p>
            <p className="text-xs text-slate-500">still needed</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{fmtH(data.avg_per_week)}</p>
            <p className="text-xs text-slate-500">avg/week for {data.weeks_left}w left</p>
          </div>
        </div>
        <div className="w-full bg-amber-100 rounded-full h-3 mb-2">
          <div
            className={`h-3 rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>{pct}% complete</span>
          <span>{data.target} hrs / year</span>
        </div>
        <p className="text-xs text-amber-700 mt-3 italic">
          Indicative only — verify with your accountant.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Breakdown Cards ────────────────────────────────────────────────────────

function BreakdownCards({ summary }) {
  if (!summary) return null;
  const { by_project, by_activity } = summary;
  const totalProj = Object.values(by_project).reduce((s, v) => s + v, 0);
  const totalAct  = Object.values(by_activity).reduce((s, v) => s + v, 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">Hours by Project (year)</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {Object.entries(by_project).map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-sm mb-0.5">
                <span className="font-medium">{k}</span>
                <span className="text-slate-500">{fmtH(v)}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-violet-400" style={{ width: `${totalProj > 0 ? (v / totalProj) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
          {Object.keys(by_project).length === 0 && <p className="text-xs text-slate-400">No data yet</p>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">Hours by Activity (year)</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {Object.entries(by_activity).slice(0, 8).map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-sm mb-0.5">
                <span className="font-medium capitalize">{k}</span>
                <span className="text-slate-500">{fmtH(v)}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-blue-400" style={{ width: `${totalAct > 0 ? (v / totalAct) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
          {Object.keys(by_activity).length === 0 && <p className="text-xs text-slate-400">No data yet</p>}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function WorkingHours() {
  const { getAuthHeaders } = useAuth();

  const [viewMode,    setViewMode]    = useState("list");
  const [entries,     setEntries]     = useState([]);
  const [weekEntries, setWeekEntries] = useState([]);
  const [summary,     setSummary]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [weekLoading, setWeekLoading] = useState(false);
  const [sumLoading,  setSumLoading]  = useState(true);

  // Modals
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editEntry,    setEditEntry]    = useState(null);
  const [quickLogOpen, setQuickLogOpen] = useState(false);
  const [quickLogDate, setQuickLogDate] = useState(null);
  const [deleteId,     setDeleteId]     = useState(null);

  // List-view filters
  const [dateFrom,       setDateFrom]       = useState("");
  const [dateTo,         setDateTo]         = useState("");
  const [filterProject,  setFilterProject]  = useState("all");
  const [filterActivity, setFilterActivity] = useState("all");

  // Timesheet week
  const [weekStart, setWeekStart] = useState(() => getWeekMonday(new Date()));

  const authHeaders = { headers: getAuthHeaders() };

  // ── Data loaders ────────────────────────────────────────────────────────

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom)                params.date_from     = dateFrom;
      if (dateTo)                  params.date_to       = dateTo;
      if (filterProject !== "all") params.project       = filterProject;
      if (filterActivity !== "all")params.activity_type = filterActivity;
      const res = await axios.get(`${API}/working-hours`, { ...authHeaders, params });
      setEntries(res.data || []);
    } catch {
      toast.error("Failed to load entries");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterProject, filterActivity, getAuthHeaders]);

  const loadWeekEntries = useCallback(async () => {
    const days = getWeekDays(weekStart);
    setWeekLoading(true);
    try {
      const res = await axios.get(`${API}/working-hours`, {
        ...authHeaders,
        params: { date_from: days[0], date_to: days[6] },
      });
      setWeekEntries(res.data || []);
    } catch {
      // non-fatal
    } finally {
      setWeekLoading(false);
    }
  }, [weekStart, getAuthHeaders]);

  const loadSummary = useCallback(async () => {
    setSumLoading(true);
    try {
      const res = await axios.get(`${API}/working-hours/summary`, authHeaders);
      setSummary(res.data);
    } catch {
      // non-fatal
    } finally {
      setSumLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadEntries(); },    [loadEntries]);
  useEffect(() => { loadWeekEntries(); },[loadWeekEntries]);
  useEffect(() => { loadSummary(); },    [loadSummary]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const refreshAll = () => { loadEntries(); loadWeekEntries(); loadSummary(); };

  const handleSaved = () => {
    setModalOpen(false);
    setEditEntry(null);
    setQuickLogOpen(false);
    setQuickLogDate(null);
    refreshAll();
  };

  const handleEdit = (entry) => { setEditEntry(entry); setModalOpen(true); };
  const handleAdd  = (defaultDate) => {
    setEditEntry(defaultDate ? { ...EMPTY_FORM, date: defaultDate } : null);
    setModalOpen(true);
  };

  const handleAddForDay = (dateStr) => { handleAdd(dateStr); };

  const handleDuplicate = (entry) => {
    const { id, created_at, updated_at, ...rest } = entry;
    setEditEntry({ ...rest, date: todayStr() });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/working-hours/${deleteId}`, authHeaders);
      toast.success("Entry deleted");
      refreshAll();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteId(null);
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (dateFrom)                params.set("date_from",     dateFrom);
    if (dateTo)                  params.set("date_to",       dateTo);
    if (filterProject !== "all") params.set("project",       filterProject);
    if (filterActivity !== "all")params.set("activity_type", filterActivity);
    axios.get(`${API}/working-hours/export?${params}`, { ...authHeaders, responseType: "blob" })
      .then(res => {
        const blob = new Blob([res.data], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = res.headers["content-disposition"]?.match(/filename="([^"]+)"/)?.[1] || "working_hours.csv";
        a.click();
      }).catch(() => toast.error("Export failed"));
  };

  const totalFiltered = entries.reduce((s, e) => s + (e.total_hours || 0), 0);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">

        {/* ── Header ── */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Working Hours</h1>
            <p className="text-slate-500 mt-1">Track hours for tax / urencriterium administration</p>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/* View toggle */}
            <div className="flex border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  viewMode === "list" ? "bg-purple-600 text-white" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                <List className="w-3.5 h-3.5" />
                List
              </button>
              <button
                onClick={() => setViewMode("timesheet")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200 ${
                  viewMode === "timesheet" ? "bg-purple-600 text-white" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Timesheet
              </button>
            </div>

            <Button variant="outline" size="sm" onClick={refreshAll}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="w-4 h-4 mr-1.5" />
              Export
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => { setQuickLogDate(todayStr()); setQuickLogOpen(true); }}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Zap className="w-4 h-4 mr-1.5" />
              Quick Log
            </Button>
            <Button
              size="sm"
              onClick={() => handleAdd()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add Entry
            </Button>
          </div>
        </div>

        {/* ── Summary ── */}
        <SummaryCards summary={summary} loading={sumLoading} />
        <UrenCard data={summary?.urencriterium} />
        <BreakdownCards summary={summary} />

        {/* ── LIST MODE ── */}
        {viewMode === "list" && (
          <>
            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <Label className="text-xs mb-1 block">From</Label>
                    <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">To</Label>
                    <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Project</Label>
                    <Select value={filterProject} onValueChange={setFilterProject}>
                      <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Projects</SelectItem>
                        {PROJECTS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Activity</Label>
                    <Select value={filterActivity} onValueChange={setFilterActivity}>
                      <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Activities</SelectItem>
                        {ACTIVITY_TYPES.map(a => (
                          <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(dateFrom || dateTo || filterProject !== "all" || filterActivity !== "all") && (
                    <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600"
                      onClick={() => { setDateFrom(""); setDateTo(""); setFilterProject("all"); setFilterActivity("all"); }}>
                      Clear filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-14">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
                  </div>
                ) : entries.length === 0 ? (
                  <div className="text-center py-16">
                    <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">No entries yet</p>
                    <p className="text-sm text-slate-400 mt-1">Use "Add Entry" or "Quick Log" to start tracking</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Work Date</TableHead>
                          <TableHead>Project</TableHead>
                          <TableHead>Activity</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Times</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead>Billable</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map(entry => {
                          const recDate = fmtAudit(entry.created_at);
                          const workDateFmt = entry.date ? format(parseISO(entry.date), "d MMM yyyy") : "—";
                          const isBackdated = entry.created_at && entry.date && entry.date < entry.created_at.slice(0, 10);
                          return (
                            <TableRow key={entry.id} className="hover:bg-slate-50">
                              <TableCell className="whitespace-nowrap">
                                <p className="font-medium text-slate-700">{workDateFmt}</p>
                                {isBackdated && recDate && (
                                  <p className="text-xs text-slate-400">Rec. {recDate}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${PROJECT_COLORS[entry.project] || "bg-slate-100 text-slate-600"}`}>
                                  {entry.project}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm capitalize text-slate-600">{entry.activity_type}</span>
                              </TableCell>
                              <TableCell className="max-w-[180px]">
                                <span className="text-sm text-slate-500 truncate block">{entry.description || "—"}</span>
                              </TableCell>
                              <TableCell className="font-mono text-xs text-slate-400 whitespace-nowrap">
                                {entry.start_time && entry.end_time
                                  ? `${entry.start_time}–${entry.end_time}${entry.break_minutes ? ` (−${entry.break_minutes}m)` : ""}`
                                  : "—"}
                              </TableCell>
                              <TableCell className="text-right font-bold text-slate-800">
                                {fmtH(entry.total_hours || 0)}
                              </TableCell>
                              <TableCell>
                                <Badge className={entry.billable ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                                  {entry.billable ? "Billable" : "Non-bill."}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-xs text-slate-400">
                                {entry.related_reference || "—"}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="ghost" onClick={() => handleEdit(entry)} title="Edit">
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleDuplicate(entry)} title="Duplicate">
                                    <Copy className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setDeleteId(entry.id)} title="Delete"
                                    className="text-red-400 hover:text-red-600 hover:bg-red-50">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <div className="px-4 py-3 border-t border-slate-100 flex justify-between text-sm text-slate-500">
                      <span>{entries.length} {entries.length === 1 ? "entry" : "entries"}</span>
                      <span className="font-semibold text-slate-700">Total: {fmtH(totalFiltered)}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ── TIMESHEET MODE ── */}
        {viewMode === "timesheet" && (
          <WeekView
            weekStart={weekStart}
            setWeekStart={setWeekStart}
            onAddForDay={handleAddForDay}
            onEdit={handleEdit}
            onDeleteRequest={setDeleteId}
            entries={weekEntries}
            loading={weekLoading}
          />
        )}
      </div>

      {/* Modals */}
      <EntryModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditEntry(null); }}
        onSaved={handleSaved}
        initial={editEntry}
      />

      <QuickLogModal
        open={quickLogOpen}
        onClose={() => { setQuickLogOpen(false); setQuickLogDate(null); }}
        onSaved={handleSaved}
        defaultDate={quickLogDate}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entry?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
